//
// 店舗情報
//
import * as http from '@/libs/http'

//
// 初期化情報 (オブジェクトのみ)
//
const init = {
  profile: {
    pref: '',
    name: '',
    image: null,
    postal: '',
    address: '',
    access: '',
    map: '',
    phone: '',
    workTime: '',
    dayOff: '',
    intervalHour: 0,
    extra: null
  },
  calendar: {
    period: {
      begin: '1900-01-01 00:00',
      end: '1900-01-02 00:00'
    },
    dates: []
  },
  pick: {
    enabled: false,
    oneway: 0,
    round: 0
  },
  deli: {
    enabled: false,
    oneway: 0,
    round: 0
  }
}

//
// ストア
//
export default {
  namespaced: true,

  state: {
    id: 0,
    category: '',

    // 基本情報
    profile: {
      ...init.profile
    },

    // 営業カレンダー
    calendar: {
      ...init.calendar
    },

    // 車両マスタ
    cars: [],

    // 免責補償マスタ
    insurances: [],

    // オプションマスタ
    options: [],

    // 返却場所マスタ
    locations: [],

    // 送迎マスタ
    pick: {
      ...init.pick
    },

    // 配車マスタ
    deli: {
      ...init.deli
    },

    // クーポンマスタ
    coupons: []
  },
  getters: {
    // 店舗ID
    shopID (state) {
      return state.id
    },

    // カテゴリ
    category (state) {
      return state.category
    },

    // 基本情報
    profile (state) {
      return {
        shopID: state.id,
        ...state.profile
      }
    },

    // 営業カレンダー
    m_calendar (state) {
      return state.calendar
    },

    // 車両マスタ
    m_cars (state) {
      return state.cars
    },

    // 免責補償マスタ
    m_insurances (state) {
      return state.insurances
    },

    // オプションマスタ (option のみ)
    m_options (state) {
      return state.options.filter(item => item.type !== 'gear').sort((x, y) => {
        let xodr = 999
        let yodr = 999
        switch (x.type) {
          case 'navi': xodr = 0; break
          case 'seat': xodr = 1; break
          case 'pet': xodr = 2; break
        }
        switch (y.type) {
          case 'navi': yodr = 0; break
          case 'seat': yodr = 1; break
          case 'pet': yodr = 2; break
        }
        return (xodr < yodr) ? -1 : (xodr > yodr) ? 1 : 0
      })
    },

    // オプションマスタ (gear のみ)
    m_gears (state) {
      return state.options.filter(item => item.type === 'gear')
    },

    // 返却場所マスタ
    m_locations (state) {
      return state.locations
    },

    // 送迎マスタ
    m_pick (state) {
      return state.pick
    },

    // 配車マスタ
    m_deli (state) {
      return state.deli
    },

    // クーポンマスタ
    m_coupons (state) {
      return state.coupons
    }
  },
  mutations: {
    // 店舗IDを設定
    setShopID (state, shopid) {
      state.id = shopid
    },

    // 表示カテゴリを設定
    setCategory (state, category) {
      state.category = category
    },

    // 基本情報を設定
    setProfile (state, data) {
      state.profile = data || { ...init.profile }
    },

    // 営業カレンダーを設定
    setCalendar (state, data) {
      state.calendar = data || { ...init.calendar }
    },

    // 車両マスタを設定
    setCars (state, data) {
      state.cars = data || []
    },

    // 免責補償マスタを設定
    setInsurances (state, data) {
      state.insurances = data || []
    },

    // オプションマスタを設定
    setOptions (state, data) {
      state.options = data || []
    },

    // 返却場所マスタを設定
    setLocations (state, data) {
      state.locations = data || []
    },

    // 送迎マスタを設定
    setPick (state, data) {
      state.pick = data || { ...init.pick }
    },

    // 配車マスタを設定
    setDeli (state, data) {
      state.deli = data || { ...init.deli }
    },

    // クーポンマスタを設定
    setCoupons (state, data) {
      state.coupons = data || []
    }
  },
  actions: {
    //
    // 店舗情報をクリアする
    //
    clearShop ({ commit }) {
      commit('setCategory', '')
      commit('setCars', null)
      commit('setInsurances', null)
      commit('setOptions', null)
      commit('setPick', null)
      commit('setDeli', null)
    },

    //
    // 店舗IDを設定する
    // 同時に基本情報などを読み込む
    //
    async setShopID ({ state, commit }, shopid) {
      // shopid が現在のものと異なるなら、store を一旦クリアする
      if (state.id !== shopid) {
        commit('setShopID', shopid)
        commit('setProfile', null)
        commit('setCalendar', null)
      }
      if (shopid === 0) {
        return true
      }

      // 店舗情報を読み込む
      const values = await Promise.all([
        http.request(async api => {
          const resp = await api.get(`shops/${shopid}/profile`)
          if (resp.status === http.OK) {
            return resp.data
          }
          return new http.StatusError(resp)
        }),
        http.request(async api => {
          const resp = await api.get(`shops/${shopid}/calendar?cat=1`)
          if (resp.status === http.OK) {
            return resp.data
          }
          return new http.StatusError(resp)
        })
      ])
      if (values[0] === http.FAILED || values[1] === http.FAILED) {
        return false
      }

      // 取得した結果を commit する
      const profile = values[0]
      commit('setProfile', {
        pref: profile.prefName,
        name: profile.dispName || profile.shopName,
        image: profile.image,
        postal: profile.postal,
        address: profile.address1 + profile.address2,
        access: profile.access,
        map: profile.map,
        phone: profile.phone,
        workTime: profile.workTime.special,
        dayOff: profile.dayOff,
        intervalHour: profile.intervalHour.special,
        extra: profile.extra
      })
      commit('setCalendar', values[1])

      return true
    },

    //
    // 表示カテゴリを設定する
    // 同時にマスタなどを読み込む
    //
    async setCategory ({ state, commit }, category) {
      // category が現在のものと異なるなら、store を一旦クリアする
      if (state.category !== category) {
        commit('setCategory', category)
        commit('setCars', null)
        commit('setInsurances', null)
        commit('setOptions', null)
        commit('setPick', null)
        commit('setDeli', null)
        commit('setCoupons', null)
      }
      if (category === '') {
        return true
      }

      // マスタを読み込む
      const masters = await http.request(async api => {
        const resp = await api.get(`shops/${state.id}/special-masters`)
        if (resp.status === http.OK) {
          return resp.data
        }
        return new http.StatusError(resp)
      })
      if (masters === http.FAILED) {
        return false
      }

      let insurances = []
      if (masters.insurances || masters.insurances.length > 0) {
        insurances = [
          ...masters.insurances,
          // 最後に なし を追加する
          {
            id: 0,
            name: 'なし',
            price: 0
          }
        ]
      }

      let locations = []
      if (masters.locations || masters.locations.length > 0) {
        locations = [
          // 最初に なし を追加する
          {
            id: 0,
            name: 'なし',
            price: 0
          },
          ...masters.locations
        ]
      }

      commit('setCars', masters.cars)
      commit('setInsurances', insurances)
      commit('setOptions', masters.options)
      commit('setLocations', locations)
      commit('setPick', masters.pick)
      commit('setDeli', masters.deli)
      commit('setCoupons', masters.coupons)
      return true
    }
  }
}
